<script setup>
import headerNavigation from '@/config/header-navigation';

import routes from '@/constants/routes';
import { dropdownTriggers } from '@/constants/dropdown';

const props = defineProps({
  referenceRef: {
    type: Object,
    default: undefined,
  },

  showSwitches: {
    type: Boolean,
    default: true,
  },
  showSearchButton: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits([
  'visibilityChange',
  'redirect',
  'searchClick',
  'accountClick',
]);

const { t } = useI18n();

// dropdown
const dropdownRef = ref(null);

const isDropdownOpened = computed(() => {
  if (!dropdownRef.value) return;

  return dropdownRef.value.visible;
});

function closeDropdown() {
  if (dropdownRef.value && dropdownRef.value.visible) dropdownRef.value.close();
}

watch(isDropdownOpened, (value) => {
  emit('visibilityChange', value);
});

// account navigation
const authStore = useAuthStore();
const accountNavigation = computed(() => {
  const items = [];

  if (props.showSearchButton) {
    items.push({
      text: 'mobileMenu.buttons.search',
      onClick: () => {
        emit('searchClick');
        closeDropdown();
      },
    });
  }

  if (authStore.account?.activeOrderTicketsCount || !authStore.isGuest) {
    items.push({
      text: 'mobileMenu.buttons.myTickets',
      count: authStore.account?.activeOrderTicketsCount,
      link: routes.MY_TICKETS,
    });
  }

  if (authStore.account?.activeCartTicketsCount) {
    items.push({
      text: 'mobileMenu.buttons.cart',
      count: authStore.account?.activeCartTicketsCount,
      link: routes.CART,
    });
  }

  if (items.length > 0) {
    items.push({
      text: 'mobileMenu.buttons.account',
      onClick: () => {
        emit('accountClick');
        closeDropdown();
      },
    });
  }

  return items;
});

// redirect
function handleRedirect(link) {
  closeDropdown();
  if (link) emit('redirect', link);
}
</script>

<template>
  <UiDropdown
    ref="dropdownRef"
    class="ui-dropdown-menu"
    :trigger-event="dropdownTriggers.click"
    :reference-ref="referenceRef"
    :gap="-1"
    use-v-if
  >
    <template #trigger="{ visible }">
      <button class="ui-dropdown-menu__button" aria-label="Burger">
        <CIcon
          class="ui-dropdown-menu__icon"
          :name="!visible ? 'burger' : 'cross'"
        />
      </button>
    </template>

    <template #content>
      <div
        class="ui-dropdown-menu__wrapper"
        :class="{ 'ui-dropdown-menu__wrapper--extended': showSwitches }"
      >
        <div class="ui-dropdown-menu__content">
          <div v-if="showSwitches" class="ui-dropdown-menu__switches">
            <BundlesCurrencySelect
              class="ui-dropdown-menu__switch"
              :trigger-event="dropdownTriggers.click"
            >
              <span class="ui-dropdown-menu__font">
                {{ t('mobileMenu.currency') }}
              </span>
            </BundlesCurrencySelect>

            <BundlesLangSelect
              class="ui-dropdown-menu__switch"
              :trigger-event="dropdownTriggers.click"
            >
              <span class="ui-dropdown-menu__font">
                {{ t('mobileMenu.language') }}
              </span>
            </BundlesLangSelect>
          </div>

          <div class="ui-dropdown-menu__columns">
            <ListsMenuLinks
              class="ui-dropdown-menu__column"
              :items="headerNavigation"
              @redirect="handleRedirect"
            />
            <ListsMenuLinks
              v-if="accountNavigation.length"
              class="ui-dropdown-menu__column"
              :items="accountNavigation"
              @redirect="handleRedirect"
            />
          </div>
        </div>
      </div>
    </template>
  </UiDropdown>
</template>
